import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
  SellerTicketAverageRow,
  SellerTicketAverageSummary,
} from '../types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const exportToCSV = (filename: string, rows: SellerTicketAverageRow[], summary: SellerTicketAverageSummary): void => {
  let csv = 'sep=;\n'
  csv += 'Posição;Vendedor;Pedidos;Participação (%)\n'

  rows.forEach((row, index) => {
    const participation = summary.totalOrders > 0 ? (row.orderCount / summary.totalOrders) * 100 : 0
    csv += `${index + 1};"${row.sellerLabel}";${row.orderCount};${participation.toFixed(1)}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: SellerTicketAverageRow[], summary: SellerTicketAverageSummary): void => {
  const workbookRows = rows.map((row, index) => {
    const participation = summary.totalOrders > 0 ? (row.orderCount / summary.totalOrders) * 100 : 0
    return {
      Posição: index + 1,
      Vendedor: row.sellerLabel,
      Pedidos: row.orderCount,
      'Participação (%)': participation.toFixed(1),
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(workbookRows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Volume')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, rows: SellerTicketAverageRow[], summary: SellerTicketAverageSummary): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Volume de Pedidos por Vendedor', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Indicadores Consolidados', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Pedidos totais: ${summary.totalOrders} | Vendedores com pedidos: ${rows.length}`, margin, yPosition)
  yPosition += 5

  yPosition += 5

  autoTable(pdf, {
    startY: yPosition,
    head: [['Pos', 'Vendedor', 'Pedidos', 'Participação (%)']],
    body: rows.map((row, index) => {
      const participation = summary.totalOrders > 0 ? (row.orderCount / summary.totalOrders) * 100 : 0
      return [index + 1, row.sellerLabel, row.orderCount, participation.toFixed(1)]
    }),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: {
      1: { halign: 'left' },
      2: { halign: 'center' },
      3: { halign: 'center' },
    },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const OrderVolumeReportPage: FC = () => {
  const [filters, setFilters] = useState<SellerTicketAverageFilters>({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const topByOrders = useMemo(() => {
    if (!report?.rows.length) {
      return null
    }

    return [...report.rows].sort((left, right) => right.orderCount - left.orderCount)[0]
  }, [report])

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle breadcrumbs={[{ title: 'Volume de Pedidos por Vendedor', path: '/seller/reports/order-volume', isActive: true }]}>
        Relatório de Volume de Pedidos
      </PageTitle>

      <ReportShell
        title='Volume de Pedidos por Vendedor'
        description='Acompanhamento de volume de pedidos por vendedor no período selecionado, com participação percentual e distribuição por origem.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button
              className='btn btn-light-success'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToCSV(`Volume-Pedidos-${timestamp}.csv`, report?.rows || [], report?.summary || { totalOrders: 0, totalAmountCents: 0, averageTicketCents: 0 })
              }}
              disabled={loading || !report?.rows.length}
              title='Baixar relatório em CSV'
            >
              <i className='fas fa-file-csv me-2'></i>
              CSV
            </button>
            <button
              className='btn btn-light-info'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToXLSX(`Volume-Pedidos-${timestamp}.xlsx`, report?.rows || [], report?.summary || { totalOrders: 0, totalAmountCents: 0, averageTicketCents: 0 })
              }}
              disabled={loading || !report?.rows.length}
              title='Baixar relatório em XLSX'
            >
              <i className='fas fa-file-excel me-2'></i>
              XLSX
            </button>
            <button
              className='btn btn-light-danger'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToPDF(`Volume-Pedidos-${timestamp}.pdf`, report?.rows || [], report?.summary || { totalOrders: 0, totalAmountCents: 0, averageTicketCents: 0 })
              }}
              disabled={loading || !report?.rows.length}
              title='Baixar relatório em PDF'
            >
              <i className='fas fa-file-pdf me-2'></i>
              PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Pedidos totais</div>
                <div className='fs-2hx fw-bold text-primary'>
                  {(report?.summary.totalOrders || 0).toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Vendedores com pedidos</div>
                <div className='fs-2hx fw-bold text-info'>
                  {(report?.rows.length || 0).toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior volume</div>
                <div className='fs-5 fw-bold text-dark'>
                  {topByOrders ? <SellerIdentity label={topByOrders.sellerLabel} /> : 'Sem dados'}
                </div>
                <div className='text-gray-700 fw-semibold mt-2'>
                  {topByOrders ? `${topByOrders.orderCount} pedidos` : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Ranking por quantidade de pedidos</span>
              <span className='text-gray-600 mt-1'>Participação de pedidos por vendedor no período.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : report && report.rows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Posição</th>
                      <th>Vendedor</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Participação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...report.rows]
                      .sort((left, right) => right.orderCount - left.orderCount)
                      .map((row, index) => {
                        const participation =
                          report.summary.totalOrders > 0
                            ? (row.orderCount / report.summary.totalOrders) * 100
                            : 0

                        return (
                          <tr key={row.sellerKey}>
                            <td>#{index + 1}</td>
                            <td>
                              <div className='fw-bold text-dark'>
                                <SellerIdentity label={row.sellerLabel} />
                              </div>
                            </td>
                            <td className='text-end fw-bold'>{row.orderCount}</td>
                            <td className='text-end'>
                              {percentFormatter.format(participation)}%
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum pedido encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { OrderVolumeReportPage }